<template>
  <section class="platform_guide">
    <div class="plat_title">
      <img src="./images/plat_title.png" alt="" />
    </div>
    <p class="guide_intro">以下平台已接入妙想MCP/Skills，速看如何快捷调用~</p>
    <div class="guide_card">
      <h2 class="guide_title">通用添加方式</h2>
      <p class="guide_description">
        一句话安装:将下面的内容贴给你的本地Agent来实现妙想MCP/Skills配置
      </p>
      <div class="guide_prompt">{{ installPrompt }}</div>
      <div class="copy_btn_wrap">
        <button class="copy_btn" type="button" @click="handleCopy">
          一键复制
        </button>
        <WxOpenAppItm
          v-if="hasAuth && appInfo.isWeChat"
          @linkAppMed="linkAppMed"
        />
      </div>
    </div>

    <!-- 各端配置指南 -->
    <section class="platform_config_guide">
      <div class="config_header">
        <div class="config_heading">
          <h2 class="config_title">各端配置指南</h2>
          <p class="config_subtitle">更多平台接入中...</p>
        </div>
        <div class="document_link_wrap">
          <button
            class="document_link"
            type="button"
            @click="handleViewPlatformDoc"
          >
            <img class="document_icon" src="./images/platform_pdf.png" alt="" />
            <span>全平台操作文档</span>
            <img
              class="document_arrow"
              src="./images/platform_arrow.png"
              alt=""
            />
          </button>
          <WxOpenAppItm
            v-if="hasAuth && appInfo.isWeChat"
            @linkAppMed="linkAppMed"
          />
        </div>
      </div>

      <div class="platform_tabs" aria-label="配置平台">
        <button
          v-for="platform in platforms"
          :key="platform.id"
          class="platform_tab"
          :class="{ platform_active: activePlatform.id === platform.id }"
          type="button"
          :aria-pressed="activePlatform.id === platform.id"
          @click="selectPlatform(platform)"
        >
          <img
            v-if="platform.logo"
            class="platform_logo"
            :src="platform.logo"
            alt=""
          />
          <span
            v-else
            class="platform_logo logo_placeholder"
            aria-hidden="true"
          ></span>
          <span class="platform_name">{{ platform.name }}</span>
        </button>
      </div>

      <ol v-if="activePlatform.steps.length" class="config_steps">
        <li
          v-for="(step, index) in activePlatform.steps"
          :key="index"
          class="config_step"
        >
          <span class="step_number">{{ index + 1 }}</span>
          <div class="step_content">
            <h3 class="step_title">
              <span
                v-for="(part, partIndex) in step.title"
                :key="partIndex"
                :class="{ step_highlight: part.highlight }"
                >{{ part.text }}</span
              >
            </h3>
            <button
              v-for="(image, imageIndex) in step.images"
              :key="image.src"
              class="step_image"
              type="button"
              :aria-label="`放大${activePlatform.name}第${index + 1}步第${
                imageIndex + 1
              }张截图`"
              @click="openPreview(image.src, $event)"
            >
              <img
                :src="image.thumbnail"
                :width="image.width"
                :height="image.height"
                :alt="`${activePlatform.name}配置步骤${index + 1}：${step.title
                  .map((part) => part.text)
                  .join('')}`"
                loading="lazy"
              />
              <span class="image_caption">点击放大</span>
            </button>
          </div>
        </li>
      </ol>
      <p v-else class="config_empty">更多平台接入中...</p>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onDeactivated, ref } from "vue";
import { storeToRefs } from "pinia";
import { api as createViewer } from "v-viewer";
import "viewerjs/dist/viewer.css";
import { appInfo, openDeepLink } from "@eastmoney/mku";
import WxOpenAppItm from "@/components/WxOpenAppItm.vue";
import { useMainStore } from "@/pinia/main";
import { copyContent } from "@/utils/common";
import copyText from "@/utils/copy";
import { $toast } from "@/utils/globalUtils";
import workbuddyLogo from "./images/platforms/workbuddy.png";
import doubaoLogo from "./images/platforms/doubao.png";
import imaLogo from "./images/platforms/ima.svg";
import kimiLogo from "./images/platforms/kimi.png";
import qianwenLogo from "./images/platforms/qianwen.png";
import baidudaziLogo from "./images/platforms/baidudazi.png";
import workbuddy12Image from "./images/platform-guides/previews/workbuddy_1_2.png";
import workbuddy12Thumbnail from "./images/platform-guides/thumbnails/workbuddy_1_2.png";
import login21Image from "./images/platform-guides/previews/login_2_1.png";
import login21Thumbnail from "./images/platform-guides/thumbnails/login_2_1.png";
import workbuddy31Image from "./images/platform-guides/previews/workbuddy_3_1.png";
import workbuddy31Thumbnail from "./images/platform-guides/thumbnails/workbuddy_3_1.png";
import workbuddy32Image from "./images/platform-guides/previews/workbuddy_3_2.png";
import workbuddy32Thumbnail from "./images/platform-guides/thumbnails/workbuddy_3_2.png";
import doubao11Image from "./images/platform-guides/previews/doubao_1_1.png";
import doubao11Thumbnail from "./images/platform-guides/thumbnails/doubao_1_1.png";
import doubao32Image from "./images/platform-guides/previews/doubao_3_2.png";
import doubao32Thumbnail from "./images/platform-guides/thumbnails/doubao_3_2.png";
import doubao31Image from "./images/platform-guides/previews/doubao_3_1.png";
import doubao31Thumbnail from "./images/platform-guides/thumbnails/doubao_3_1.png";
import ima12Image from "./images/platform-guides/previews/ima_1_2.png";
import ima12Thumbnail from "./images/platform-guides/thumbnails/ima_1_2.png";
import ima3Image from "./images/platform-guides/previews/ima_3.png";
import ima3Thumbnail from "./images/platform-guides/thumbnails/ima_3.png";
import kimi1Image from "./images/platform-guides/previews/kimi_1.png";
import kimi1Thumbnail from "./images/platform-guides/thumbnails/kimi_1.png";
import kimi2Image from "./images/platform-guides/previews/kimi_2.png";
import kimi2Thumbnail from "./images/platform-guides/thumbnails/kimi_2.png";
import kimi3Image from "./images/platform-guides/previews/kimi_3.png";
import kimi3Thumbnail from "./images/platform-guides/thumbnails/kimi_3.png";
import qianwen1Image from "./images/platform-guides/previews/qianwen_1.png";
import qianwen1Thumbnail from "./images/platform-guides/thumbnails/qianwen_1.png";
import qianwen2Image from "./images/platform-guides/previews/qianwen_2.png";
import qianwen2Thumbnail from "./images/platform-guides/thumbnails/qianwen_2.png";
import qianwen4Image from "./images/platform-guides/previews/qianwen_4.png";
import qianwen4Thumbnail from "./images/platform-guides/thumbnails/qianwen_4.png";
import baidudazi12Image from "./images/platform-guides/previews/baidudazi_1_2.png";
import baidudazi12Thumbnail from "./images/platform-guides/thumbnails/baidudazi_1_2.png";
import baidudazi31Image from "./images/platform-guides/previews/baidudazi_3_1.png";
import baidudazi31Thumbnail from "./images/platform-guides/thumbnails/baidudazi_3_1.png";
import baidudazi32Image from "./images/platform-guides/previews/baidudazi_3_2.png";
import baidudazi32Thumbnail from "./images/platform-guides/thumbnails/baidudazi_3_2.png";

const { hasAuth } = storeToRefs(useMainStore());

const linkAppMed = () => {
  window.linkAppMed();
};

const handleViewPlatformDoc = () => {
  appInfo.inApp(() => {
    openDeepLink(encodeURIComponent("https://dl.dfcfs.com/m/emxu"));
  }, linkAppMed);
};

const installPrompt =
  "帮我按照 https://mxapi.eastmoney.com/mxds/doc/mcp-install.md 来配置妙想 MCP Server（优先使用 curl 拉取文档）";

const showCopyResult = (isSuccess: boolean) => {
  $toast.show(isSuccess ? "复制成功" : "复制失败，请长按手动复制", 3000);
};

const copyInBrowser = async () => {
  try {
    await copyText(installPrompt);
    showCopyResult(true);
  } catch {
    showCopyResult(false);
  }
};

const handleCopy = () => {
  appInfo.inApp(() => {
    try {
      copyContent({
        content: installPrompt,
        callback: (isSuccess: boolean) => {
          if (isSuccess) {
            showCopyResult(true);
          } else {
            copyInBrowser();
          }
        },
      });
    } catch {
      copyInBrowser();
    }
  }, linkAppMed);
};
type GuideStep = {
  title: { text: string; highlight?: boolean }[];
  images: { src: string; thumbnail: string; width: number; height: number }[];
};

type Platform = {
  id: string;
  name: string;
  logo: string;
  steps: GuideStep[];
};

// 接入步骤及截图提取自 https://choice.eastmoney.com/mcp/guide 。
const platforms: Platform[] = [
  {
    id: "workbuddy",
    name: "WorkBuddy",
    logo: workbuddyLogo,
    steps: [
      {
        title: [
          { text: "专家·技能·连接器", highlight: true },
          { text: "中找到妙想MCP" },
        ],
        images: [
          {
            src: workbuddy12Image,
            thumbnail: workbuddy12Thumbnail,
            width: 960,
            height: 606,
          },
        ],
      },
      {
        title: [
          {
            text: "点击MCP加号，用妙想/Choice/东方财富APP扫码或手机号授权登录",
          },
        ],
        images: [
          {
            src: login21Image,
            thumbnail: login21Thumbnail,
            width: 801,
            height: 460,
          },
        ],
      },
      {
        title: [
          { text: "点击" },
          { text: "去试试", highlight: true },
          { text: "或在" },
          { text: "对话窗口", highlight: true },
          { text: "选择" },
        ],
        images: [
          {
            src: workbuddy31Image,
            thumbnail: workbuddy31Thumbnail,
            width: 960,
            height: 738,
          },
          {
            src: workbuddy32Image,
            thumbnail: workbuddy32Thumbnail,
            width: 960,
            height: 738,
          },
        ],
      },
    ],
  },
  {
    id: "doubao",
    name: "豆包工作",
    logo: doubaoLogo,
    steps: [
      {
        title: [
          { text: "技能·连接器·伙伴", highlight: true },
          { text: "中找到东方财富妙想MCP" },
        ],
        images: [
          {
            src: doubao11Image,
            thumbnail: doubao11Thumbnail,
            width: 960,
            height: 639,
          },
        ],
      },
      {
        title: [
          {
            text: "点击立即连接，用妙想/Choice/东方财富APP扫码或手机号授权登录",
          },
        ],
        images: [
          {
            src: login21Image,
            thumbnail: login21Thumbnail,
            width: 801,
            height: 460,
          },
        ],
      },
      {
        title: [
          { text: "在对话中" },
          { text: "试用", highlight: true },
          { text: "或" },
          { text: "点击连接器", highlight: true },
          { text: "选择东方财富妙想MCP" },
        ],
        images: [
          {
            src: doubao32Image,
            thumbnail: doubao32Thumbnail,
            width: 960,
            height: 639,
          },
          {
            src: doubao31Image,
            thumbnail: doubao31Thumbnail,
            width: 960,
            height: 639,
          },
        ],
      },
    ],
  },
  {
    id: "ima",
    name: "IMA",
    logo: imaLogo,
    steps: [
      {
        title: [
          { text: "发现MCP", highlight: true },
          { text: "中找到妙想MCP" },
        ],
        images: [
          {
            src: ima12Image,
            thumbnail: ima12Thumbnail,
            width: 960,
            height: 658,
          },
        ],
      },
      {
        title: [
          { text: "点击连接，用妙想/Choice/东方财富APP扫码或手机号授权登录" },
        ],
        images: [
          {
            src: login21Image,
            thumbnail: login21Thumbnail,
            width: 801,
            height: 460,
          },
        ],
      },
      {
        title: [{ text: "对话框中调用" }],
        images: [
          {
            src: ima3Image,
            thumbnail: ima3Thumbnail,
            width: 960,
            height: 658,
          },
        ],
      },
    ],
  },
  {
    id: "kimi",
    name: "Kimi Work",
    logo: kimiLogo,
    steps: [
      {
        title: [
          { text: "插件市场", highlight: true },
          { text: "找到东方财富妙想金融数据" },
        ],
        images: [
          {
            src: kimi1Image,
            thumbnail: kimi1Thumbnail,
            width: 960,
            height: 591,
          },
        ],
      },
      {
        title: [{ text: "点击安装东方财富妙想金融数据" }],
        images: [
          {
            src: kimi2Image,
            thumbnail: kimi2Thumbnail,
            width: 960,
            height: 591,
          },
        ],
      },
      {
        title: [{ text: "对话框中调用东方财富妙想金融数据插件" }],
        images: [
          {
            src: kimi3Image,
            thumbnail: kimi3Thumbnail,
            width: 960,
            height: 591,
          },
        ],
      },
    ],
  },
  {
    id: "qianwen",
    name: "千问办公",
    logo: qianwenLogo,
    steps: [
      {
        title: [
          { text: "连接器", highlight: true },
          { text: "找到东方财富妙想MCP" },
        ],
        images: [
          {
            src: qianwen1Image,
            thumbnail: qianwen1Thumbnail,
            width: 960,
            height: 582,
          },
        ],
      },
      {
        title: [{ text: "点击安装东方财富妙想MCP" }],
        images: [
          {
            src: qianwen2Image,
            thumbnail: qianwen2Thumbnail,
            width: 960,
            height: 582,
          },
        ],
      },
      {
        title: [
          { text: "点击启用，用妙想/Choice/东方财富APP扫码或手机号授权登录" },
        ],
        images: [
          {
            src: login21Image,
            thumbnail: login21Thumbnail,
            width: 801,
            height: 460,
          },
        ],
      },
      {
        title: [
          { text: "对话框选择" },
          { text: "连接器", highlight: true },
          { text: "，调用东方财富妙想MCP" },
        ],
        images: [
          {
            src: qianwen4Image,
            thumbnail: qianwen4Thumbnail,
            width: 960,
            height: 582,
          },
        ],
      },
    ],
  },
  {
    id: "baidudazi",
    name: "百度搭子",
    logo: baidudaziLogo,
    steps: [
      {
        title: [
          { text: "专家套件·技能", highlight: true },
          { text: "中找到东方财富妙想MCP，点击开启连接" },
        ],
        images: [
          {
            src: baidudazi12Image,
            thumbnail: baidudazi12Thumbnail,
            width: 960,
            height: 591,
          },
        ],
      },
      {
        title: [{ text: "用妙想/Choice/东方财富APP扫码或手机号授权登录" }],
        images: [
          {
            src: login21Image,
            thumbnail: login21Thumbnail,
            width: 801,
            height: 460,
          },
        ],
      },
      {
        title: [{ text: "应用启用后在对话框调用" }],
        images: [
          {
            src: baidudazi31Image,
            thumbnail: baidudazi31Thumbnail,
            width: 960,
            height: 591,
          },
          {
            src: baidudazi32Image,
            thumbnail: baidudazi32Thumbnail,
            width: 960,
            height: 591,
          },
        ],
      },
    ],
  },
];

const activePlatform = ref<Platform>(platforms[0]!);

const previewIndex = ref(-1);
const previewImages = computed(() =>
  activePlatform.value.steps.flatMap((step, index) =>
    step.images.map((image) => ({
      src: image.src,
      thumbnail: image.thumbnail,
      description: `${activePlatform.value.name} · 步骤${
        index + 1
      }：${step.title.map((part) => part.text).join("")}`,
    })),
  ),
);
type PreviewViewer = ReturnType<typeof createViewer> & { viewer: HTMLElement };
let previewViewer: PreviewViewer | undefined;
let restorePreviewScroll: (() => void) | undefined;
let removePreviewListeners: (() => void) | undefined;
let previewTrigger: HTMLElement | null = null;

const focusWithoutScroll = (element: HTMLElement | null) => {
  if (!element) return;
  const scrollTop =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
  window.scrollTo(0, scrollTop);
};

const closePreview = async (restoreFocus = false) => {
  const trigger = previewTrigger;
  previewTrigger = null;
  const viewer = previewViewer;
  previewViewer = undefined;
  previewIndex.value = -1;
  removePreviewListeners?.();
  removePreviewListeners = undefined;
  try {
    viewer?.destroy();
  } finally {
    restorePreviewScroll?.();
    restorePreviewScroll = undefined;
  }
  if (restoreFocus) {
    await nextTick();
    if (!previewViewer) focusWithoutScroll(trigger);
  }
};

const selectPlatform = (platform: Platform) => {
  closePreview();
  activePlatform.value = platform;
};

const openPreview = async (src: string, event: MouseEvent) => {
  const index = previewImages.value.findIndex((image) => image.src === src);
  if (index === -1) return;
  if (restorePreviewScroll) return;

  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;
  const scrollTop =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  const previousBodyStyle = {
    position: bodyStyle.position,
    top: bodyStyle.top,
    left: bodyStyle.left,
    right: bodyStyle.right,
    width: bodyStyle.width,
    overflow: bodyStyle.overflow,
  };
  const previousHtmlOverflow = htmlStyle.overflow;
  bodyStyle.position = "fixed";
  bodyStyle.top = `-${scrollTop}px`;
  bodyStyle.left = "0";
  bodyStyle.right = "0";
  bodyStyle.width = "100%";
  bodyStyle.overflow = "hidden";
  htmlStyle.overflow = "hidden";
  restorePreviewScroll = () => {
    Object.assign(bodyStyle, previousBodyStyle);
    htmlStyle.overflow = previousHtmlOverflow;
    window.scrollTo(0, scrollTop);
    document.documentElement.scrollTop = scrollTop;
    document.body.scrollTop = scrollTop;
  };
  previewTrigger = event.currentTarget as HTMLElement;
  previewIndex.value = index;
  const images = previewImages.value;
  try {
    previewViewer = createViewer({
      images: images.map((image) => ({
        src: image.thumbnail,
        "data-source": image.src,
        alt: image.description,
      })),
      options: {
        className: "platform_guide_viewer",
        url: "data-source",
        initialViewIndex: index,
        navbar: false,
        focus: false,
        transition: false,
        loop: false,
        fullscreen: false,
        rotatable: false,
        scalable: false,
        movable: true,
        zoomable: true,
        zoomOnTouch: true,
        zoomOnWheel: true,
        // 库会把快速拖动误判为双击，切到原始尺寸（100%），因此关闭此切换。
        toggleOnDblclick: false,
        slideOnTouch: false,
        maxZoomRatio: 4,
        zIndex: 1000,
        title: (image: HTMLImageElement) =>
          `${previewIndex.value + 1} / ${images.length} · ${image.alt}`,
        toolbar: {
          prev: true,
          zoomOut: true,
          reset: true,
          zoomIn: true,
          next: true,
        },
        view: (event) => {
          previewIndex.value = event.detail.index;
          syncPreviewControls();
        },
        viewed: () => syncPreviewControls(),
        hidden: () => {
          // API 在 hidden 后自行销毁实例，此处只恢复组件的滚动和焦点。
          previewViewer = undefined;
          closePreview(true);
        },
      },
    }) as PreviewViewer;
  } catch {
    await closePreview(true);
    $toast.show("图片预览打开失败，请重试", 3000);
    return;
  }
  await nextTick();
  if (previewViewer) {
    const dialog = previewViewer.viewer;
    dialog.setAttribute("aria-label", "配置截图预览，可双指缩放并拖动图片");
    const labels: Record<string, string> = {
      ".viewer-button": "关闭截图预览",
      ".viewer-prev": "上一张",
      ".viewer-zoom-out": "缩小",
      ".viewer-reset": "还原图片",
      ".viewer-zoom-in": "放大",
      ".viewer-next": "下一张",
    };
    Object.keys(labels).forEach((selector) => {
      const button = dialog.querySelector<HTMLElement>(selector);
      button?.setAttribute("aria-label", labels[selector]!);
      button?.setAttribute("title", labels[selector]!);
    });
    dialog.addEventListener("keydown", keepPreviewFocus);
    const closeButton = dialog.querySelector<HTMLElement>(".viewer-button");
    const controls = Array.from(
      dialog.querySelectorAll<HTMLElement>(
        ".viewer-button, .viewer-toolbar li",
      ),
    );
    let controlTouch:
      | { button: HTMLElement; id: number; x: number; y: number }
      | undefined;
    const startControlTouch = (event: TouchEvent) => {
      const button = event.currentTarget as HTMLElement;
      const touch = event.touches.length === 1 ? event.touches[0] : undefined;
      controlTouch =
        touch && button.getAttribute("aria-disabled") !== "true"
          ? {
              button,
              id: touch.identifier,
              x: touch.clientX,
              y: touch.clientY,
            }
          : undefined;
    };
    const cancelControlTouch = () => {
      controlTouch = undefined;
    };
    const moveControlTouch = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (
        !touch ||
        event.touches.length !== 1 ||
        !controlTouch ||
        Math.abs(touch.clientX - controlTouch.x) > 10 ||
        Math.abs(touch.clientY - controlTouch.y) > 10
      ) {
        cancelControlTouch();
      }
    };
    const endControlTouch = (event: TouchEvent) => {
      const start = controlTouch;
      controlTouch = undefined;
      const touch = event.changedTouches[0];
      if (
        !start ||
        !touch ||
        event.currentTarget !== start.button ||
        start.button.getAttribute("aria-disabled") === "true" ||
        touch.identifier !== start.id ||
        event.touches.length ||
        Math.abs(touch.clientX - start.x) > 10 ||
        Math.abs(touch.clientY - start.y) > 10
      ) {
        return;
      }
      // 缩放后浏览器可能不再合成 click，补发按钮操作并阻止重复/穿透点击。
      event.preventDefault();
      event.stopPropagation();
      start.button.click();
    };
    controls.forEach((button) => {
      button.addEventListener("touchstart", startControlTouch);
      button.addEventListener("touchmove", moveControlTouch);
      button.addEventListener("touchend", endControlTouch);
      button.addEventListener("touchcancel", cancelControlTouch);
    });
    removePreviewListeners = () => {
      dialog.removeEventListener("keydown", keepPreviewFocus);
      controls.forEach((button) => {
        button.removeEventListener("touchstart", startControlTouch);
        button.removeEventListener("touchmove", moveControlTouch);
        button.removeEventListener("touchend", endControlTouch);
        button.removeEventListener("touchcancel", cancelControlTouch);
      });
    };
    syncPreviewControls();
    focusWithoutScroll(closeButton);
  }
};

const syncPreviewControls = () => {
  const dialog = previewViewer?.viewer;
  if (!dialog) return;
  [
    [".viewer-prev", previewIndex.value === 0],
    [".viewer-next", previewIndex.value === previewImages.value.length - 1],
  ].forEach(([selector, disabled]) => {
    const button = dialog.querySelector<HTMLElement>(selector as string);
    button?.setAttribute("aria-disabled", String(disabled));
    button?.setAttribute("tabindex", disabled ? "-1" : "0");
    if (disabled && document.activeElement === button) {
      focusWithoutScroll(dialog.querySelector(".viewer-button"));
    }
  });
};

const keepPreviewFocus = (event: KeyboardEvent) => {
  if (event.key !== "Tab" && event.keyCode !== 9) return;
  const dialog = event.currentTarget as HTMLElement;
  const buttons = dialog.querySelectorAll<HTMLElement>(
    '.viewer-button, .viewer-toolbar li:not([aria-disabled="true"])',
  );
  const firstButton = buttons[0];
  const lastButton = buttons[buttons.length - 1];
  if (event.shiftKey && document.activeElement === firstButton) {
    event.preventDefault();
    lastButton?.focus();
  } else if (!event.shiftKey && document.activeElement === lastButton) {
    event.preventDefault();
    firstButton?.focus();
  }
};

onDeactivated(() => closePreview());

onBeforeUnmount(() => {
  closePreview();
});
</script>

<style lang="scss" scoped>
.platform_guide {
  width: 100%;

  margin-top: 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  .plat_title {
    width: 6.31rem;
    height: 0.46rem;
    font-size: 0;
    img {
      width: 100%;
      height: 100%;
    }
  }
  .guide_intro {
    margin-top: 0.18rem;
    font-size: 0.28rem;
    color: #666666;
    text-align: center;
  }

  .guide_card {
    box-sizing: border-box;
    width: 6.86rem;
    max-width: calc(100% - 0.64rem);
    padding: 0.32rem 0.24rem;
    border: 0.01rem solid rgba(0, 0, 0, 0.2);
    border-radius: 0.24rem;
    background: #ffffff;
    margin-top: 0.3rem;
  }

  .guide_title {
    font-size: 0.32rem;
    line-height: 0.44rem;
    font-weight: bold;
    color: #333333;
  }

  .guide_description {
    margin-top: 0.08rem;
    font-size: 0.24rem;
    line-height: 0.34rem;
    color: #666666;
  }

  .guide_prompt {
    margin-top: 0.16rem;
    padding: 0.16rem 0.24rem;
    border-radius: 0.16rem;
    background: #f8f8fa;
    font-size: 0.24rem;
    line-height: 0.32rem;
    color: #333333;
    overflow-wrap: anywhere;
    word-break: break-all;
    -webkit-user-select: text;
    user-select: text;
  }

  .copy_btn_wrap {
    position: relative;
    width: 4.4rem;
    max-width: 100%;
    margin: 0.32rem auto 0;
  }

  .copy_btn {
    display: block;
    box-sizing: border-box;
    width: 100%;
    height: 0.72rem;
    padding: 0;
    border: none;
    border-radius: 0.36rem;
    background: #ff5500;
    font-family: inherit;
    font-size: 0.26rem;
    line-height: 0.72rem;
    font-weight: bold;
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
  }
}

.platform_config_guide {
  box-sizing: border-box;
  width: 100%;
  margin-top: 0.4rem;
  padding: 0 0.32rem;

  .config_header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }

  .config_heading {
    min-width: 0;
    flex-shrink: 0;
    margin-right: 0.12rem;
  }

  .config_title {
    font-size: 0.4rem;
    font-weight: bold;
    line-height: 0.5rem;
    color: #333333;
    white-space: nowrap;
  }

  .config_subtitle {
    margin-top: 0.04rem;
    font-size: 0.24rem;
    line-height: 0.34rem;
    color: #999999;
  }

  .document_link_wrap {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
  }

  .document_link {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.26rem;
    line-height: 0.36rem;
    font-weight: bold;
    color: #ff5500;
    white-space: nowrap;
    cursor: pointer;
  }

  .document_icon {
    width: 0.4rem;
    height: 0.4rem;
    margin-right: 0.08rem;
  }

  .document_arrow {
    width: 0.14rem;
    height: 0.22rem;
    margin-left: 0.08rem;
  }

  .platform_tabs {
    display: flex;
    margin-top: 0.24rem;
    // 为 iOS 滚动层留出边框绘制空间，避免底边被裁切。
    padding: 2px 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .platform_tab {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 0.8rem;
    padding: 0 0.22rem;
    border: 0.01rem solid #d5d5d5;
    border-radius: 0.24rem;
    background: #ffffff;
    font-family: inherit;
    color: #333333;
    white-space: nowrap;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;

    & + .platform_tab {
      margin-left: 0.16rem;
    }

    &.platform_active {
      border-color: #ff5500;
      color: #ff5500;
    }
  }

  .platform_logo {
    flex-shrink: 0;
    width: 0.4rem;
    height: 0.4rem;
    margin-right: 0.08rem;
    object-fit: contain;
  }

  .logo_placeholder {
    box-sizing: border-box;
    border: 0.01rem solid #eeeeee;
    border-radius: 0.08rem;
    background: #f8f8fa;
  }

  .platform_name {
    font-size: 0.26rem;
    line-height: 0.36rem;
  }

  .config_steps {
    margin: 0.48rem 0 0;
    padding: 0;
    list-style: none;
  }

  .config_step {
    position: relative;
    display: flex;
    align-items: flex-start;
    padding-bottom: 0.48rem;

    &::before {
      position: absolute;
      top: 0.56rem;
      bottom: 0.2rem;
      left: 0.19rem;
      width: 0.02rem;
      background: #f1f1f1;
      content: "";
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  .step_number {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 0.4rem;
    height: 0.4rem;
    margin-top: 0.02rem;
    margin-right: 0.16rem;
    border-radius: 0.08rem;
    background: #fff0e8;
    font-size: 0.28rem;
    line-height: 0.4rem;
    color: #ff5500;
  }

  .step_content {
    flex: 1;
    min-width: 0;
  }

  .step_title {
    font-size: 0.3rem;
    font-weight: bold;
    line-height: 0.46rem;
    color: #333333;
    overflow-wrap: break-word;
  }

  .step_highlight {
    color: #ff5500;
  }

  .step_image {
    display: block;
    position: relative;
    width: 100%;
    margin-top: 0.24rem;
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    cursor: zoom-in;
    -webkit-appearance: none;
    appearance: none;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  .image_caption {
    display: block;
    margin-top: 0.24rem;
    font-size: 0.26rem;
    line-height: 0.36rem;
    color: #999999;
    text-align: center;
  }

  .config_empty {
    margin-top: 0.48rem;
    padding: 0.64rem 0;
    font-size: 0.26rem;
    color: #999999;
    text-align: center;
  }
}
</style>

<!-- v-viewer API 将预览插入 body，样式通过专用类名限定在本组件的预览中。 -->
<style lang="scss">
.platform_guide_viewer {
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;

  .viewer-button {
    z-index: 2;
    top: 8px;
    right: 8px;
    top: calc(8px + constant(safe-area-inset-top));
    top: calc(8px + env(safe-area-inset-top));
    right: calc(8px + constant(safe-area-inset-right));
    right: calc(8px + env(safe-area-inset-right));
    width: 44px;
    height: 44px;

    &::before {
      top: 12px;
      bottom: auto;
      left: 12px;
    }
  }

  .viewer-footer {
    z-index: 1;
    box-sizing: border-box;
    padding: 0 12px 8px;
    padding-right: calc(12px + constant(safe-area-inset-right));
    padding-right: calc(12px + env(safe-area-inset-right));
    padding-bottom: calc(8px + constant(safe-area-inset-bottom));
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
    padding-left: calc(12px + constant(safe-area-inset-left));
    padding-left: calc(12px + env(safe-area-inset-left));
    background: rgba(0, 0, 0, 0.65);
  }

  .viewer-title {
    display: block;
    box-sizing: border-box;
    height: 62px;
    max-width: none;
    margin: 0;
    padding: 4px 0;
    font-family: inherit;
    font-size: 13px;
    line-height: 18px;
    color: #ffffff;
    opacity: 1;
    white-space: normal;
    word-wrap: break-word;
  }

  .viewer-toolbar > ul {
    margin: 0;
    padding: 4px;
    list-style: none;

    > li {
      width: 44px;
      height: 44px;

      &::before {
        margin: 12px;
      }

      & + li {
        margin-left: 6px;
      }

      &[aria-disabled="true"] {
        opacity: 0.35;
        cursor: default;
      }
    }
  }
}
</style>
